package ru.chm.gradereport.ui.screens.publichomework

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.data.GradeApi
import ru.chm.gradereport.model.GradeSheetDto
import ru.chm.gradereport.model.HomeworkDto
import ru.chm.gradereport.model.LessonSessionDto
import ru.chm.gradereport.ui.components.ErrorBanner
import ru.chm.gradereport.ui.components.ErrorStateCard
import ru.chm.gradereport.ui.components.LoadingBox
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.ScreenHeader
import ru.chm.gradereport.ui.components.ScreenScaffold
import ru.chm.gradereport.ui.components.SectionTitle
import ru.chm.gradereport.ui.components.SelectableText
import ru.chm.gradereport.ui.components.TabChip
import ru.chm.gradereport.ui.components.horizontalScrollOnWheel
import ru.chm.gradereport.ui.components.formatDisplayDate
import ru.chm.gradereport.ui.components.LinkedText
import ru.chm.gradereport.ui.components.softClickable
import ru.chm.gradereport.ui.screens.course.sheet.HomeworkDetailsDialog
import ru.chm.gradereport.ui.screens.course.sheet.SessionAccent
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.YellowAccent

private data class HomeworkSessionGroup(
    val session: LessonSessionDto?,
    val homeworks: List<HomeworkDto>,
)

@Composable
fun PublicHomeworkScreen(
    api: GradeApi,
    homeworkToken: String,
) {
    var sheet by remember { mutableStateOf<GradeSheetDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var viewingHomework by remember { mutableStateOf<HomeworkDto?>(null) }
    var focusDate by remember { mutableStateOf<String?>(null) }
    var dateInitialized by remember(homeworkToken) { mutableStateOf(false) }

    LaunchedEffect(homeworkToken) {
        loading = true
        error = null
        dateInitialized = false
        try {
            sheet = api.publicHomeworkSheet(homeworkToken)
        } catch (e: Exception) {
            error = e.message ?: "Домашние задания не найдены"
            sheet = null
        } finally {
            loading = false
        }
    }

    ScreenScaffold {
        ScreenHeader(
            title = sheet?.course?.name ?: "Домашние задания",
            subtitle = when {
                sheet != null ->
                    sheet!!.course.description.ifBlank { "Домашнее задание" }
                error != null -> "Не удалось открыть страницу"
                else -> "Домашнее задание"
            },
        )
        when {
            loading -> LoadingBox()
            sheet != null -> {
                ErrorBanner(error) { error = null }
                PublicHomeworkContent(
                    sheet = sheet!!,
                    focusDate = focusDate,
                    onFocusDateChange = { focusDate = it },
                    dateInitialized = dateInitialized,
                    onDateInitialized = { dateInitialized = true },
                    onOpenHomework = { viewingHomework = it },
                )
            }
            error != null -> ErrorStateCard(
                title = "ДЗ недоступны",
                message = error!!,
                hint = "Проверьте публичную ссылку. Если курс удалили или ссылку сменили — попросите новую у администратора.",
                modifier = Modifier.padding(top = 4.dp),
            )
        }
    }

    viewingHomework?.let { hw ->
        val current = sheet
        val session = current?.sessions?.firstOrNull { it.id == hw.sessionId }
        HomeworkDetailsDialog(
            homework = hw,
            sessions = current?.sessions.orEmpty(),
            session = session,
            editable = false,
            onDismiss = { viewingHomework = null },
            onSave = { _, _, _, _ -> },
            onDelete = {},
        )
    }
}

@Composable
private fun PublicHomeworkContent(
    sheet: GradeSheetDto,
    focusDate: String?,
    onFocusDateChange: (String?) -> Unit,
    dateInitialized: Boolean,
    onDateInitialized: () -> Unit,
    onOpenHomework: (HomeworkDto) -> Unit,
) {
    val orderedSessions = remember(sheet.sessions) {
        sheet.sessions.sortedWith(
            compareByDescending<LessonSessionDto> { it.date }
                .thenByDescending { it.title }
                .thenByDescending { it.id },
        )
    }
    val homeworksBySession = remember(sheet.homeworks) {
        sheet.homeworks.groupBy { it.sessionId }
    }
    val orphanHomeworks = remember(sheet) {
        val sessionIds = sheet.sessions.map { it.id }.toSet()
        sheet.homeworks
            .filter { it.sessionId == null || it.sessionId !in sessionIds }
            .sortedBy { it.title }
    }
    val sessionDates = remember(orderedSessions, homeworksBySession) {
        orderedSessions
            .filter { homeworksBySession[it.id].orEmpty().isNotEmpty() }
            .map { it.date }
            .filter { it.isNotBlank() }
            .distinct()
    }

    LaunchedEffect(sessionDates, dateInitialized) {
        if (!dateInitialized && sessionDates.isNotEmpty()) {
            onFocusDateChange(sessionDates.first())
            onDateInitialized()
        }
    }
    LaunchedEffect(sessionDates, focusDate) {
        if (focusDate != null && focusDate !in sessionDates) {
            onFocusDateChange(sessionDates.firstOrNull())
        }
    }

    val displayGroups = remember(
        orderedSessions,
        homeworksBySession,
        orphanHomeworks,
        focusDate,
    ) {
        buildList {
            orderedSessions.forEach { session ->
                val hw = homeworksBySession[session.id].orEmpty().sortedBy { it.orderIndex }
                if (hw.isEmpty()) return@forEach
                if (focusDate != null && session.date != focusDate) return@forEach
                add(HomeworkSessionGroup(session = session, homeworks = hw))
            }
            if (focusDate == null && orphanHomeworks.isNotEmpty()) {
                add(HomeworkSessionGroup(session = null, homeworks = orphanHomeworks))
            }
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        if (sheet.homeworks.isEmpty()) {
            MutedText("Пока нет домашних заданий.")
        } else {
            if (sessionDates.isNotEmpty()) {
                HomeworkDateFilter(
                    dates = sessionDates,
                    focusDate = focusDate,
                    onFocusDateChange = onFocusDateChange,
                )
            }

            if (displayGroups.isEmpty()) {
                MutedText(
                    if (focusDate != null) "На эту дату ДЗ нет."
                    else "Нет домашних заданий для отображения.",
                )
            } else {
                displayGroups.forEach { group ->
                    HomeworkSessionCard(
                        group = group,
                        onOpenHomework = onOpenHomework,
                    )
                }
            }
        }
    }
}

@Composable
private fun HomeworkDateFilter(
    dates: List<String>,
    focusDate: String?,
    onFocusDateChange: (String?) -> Unit,
) {
    val colors = GradeTheme.colors
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            "Дата",
            color = colors.textMuted,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
        )
        val datesScroll = rememberScrollState()
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .horizontalScroll(datesScroll)
                .horizontalScrollOnWheel(datesScroll),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TabChip(
                text = "Все даты",
                selected = focusDate == null,
                onClick = { onFocusDateChange(null) },
            )
            dates.forEach { date ->
                TabChip(
                    text = formatDisplayDate(date),
                    selected = focusDate == date,
                    onClick = { onFocusDateChange(date) },
                )
            }
        }
    }
}

@Composable
private fun HomeworkSessionCard(
    group: HomeworkSessionGroup,
    onOpenHomework: (HomeworkDto) -> Unit,
) {
    val colors = GradeTheme.colors
    val shape = RoundedCornerShape(14.dp)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(colors.card, shape)
            .border(1.dp, colors.border, shape)
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        val session = group.session
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            if (session == null) {
                SelectableText(
                    text = "ДЗ без занятия",
                    color = YellowAccent,
                    fontWeight = FontWeight.Bold,
                    fontSize = 15.sp,
                )
                MutedText("Старые или непривязанные задания")
            } else {
                Text(
                    "ТЕМА",
                    color = SessionAccent,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 0.5.sp,
                )
                SelectableText(
                    text = session.title.ifBlank { "Занятие" },
                    color = colors.textPrimary,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 16.sp,
                    modifier = Modifier.padding(top = 2.dp),
                )
                if (session.date.isNotBlank()) {
                    MutedText(formatDisplayDate(session.date), modifier = Modifier.padding(top = 2.dp))
                }
                if (session.description.isNotBlank()) {
                    SelectableText(
                        text = session.description,
                        color = colors.textPrimary.copy(alpha = 0.85f),
                        fontSize = 14.sp,
                        lineHeight = 20.sp,
                        modifier = Modifier.padding(top = 6.dp),
                    )
                }
            }
        }

        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            group.homeworks.forEach { hw ->
                HomeworkPublicRow(
                    homework = hw,
                    onOpen = { onOpenHomework(hw) },
                )
            }
        }
    }
}

@Composable
private fun HomeworkPublicRow(
    homework: HomeworkDto,
    onOpen: () -> Unit,
) {
    val colors = GradeTheme.colors
    val shape = RoundedCornerShape(10.dp)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(shape)
            .background(colors.nestedCard, shape)
            .border(1.dp, colors.border.copy(alpha = 0.7f), shape)
            .softClickable(
                onClick = onOpen,
                highlight = YellowAccent,
                shape = shape,
                hoverAlpha = 0.1f,
                pressAlpha = 0.18f,
            )
            .padding(horizontal = 12.dp, vertical = 10.dp),
    ) {
        SelectionContainer(modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.fillMaxWidth()) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(
                        modifier = Modifier.weight(1f).padding(end = 12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text(
                            "ДЗ",
                            color = YellowAccent,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                        )
                        Text(
                            homework.title,
                            color = colors.textPrimary,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 14.sp,
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                    ChevronRight(color = colors.textMuted)
                }
                if (homework.description.isNotBlank()) {
                    LinkedText(
                        text = homework.description,
                        color = colors.textPrimary.copy(alpha = 0.8f),
                        fontSize = 13.sp,
                        lineHeight = 18.sp,
                        modifier = Modifier.fillMaxWidth().padding(top = 6.dp),
                    )
                } else {
                    Text(
                        "Описание не указано.",
                        color = colors.textMuted,
                        fontSize = 14.sp,
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun ChevronRight(color: Color) {
    Box(
        modifier = Modifier.size(16.dp),
        contentAlignment = Alignment.Center,
    ) {
        Canvas(modifier = Modifier.size(12.dp)) {
            val stroke = 2.dp.toPx()
            val w = size.width
            val h = size.height
            drawLine(
                color = color,
                start = Offset(w * 0.3f, h * 0.18f),
                end = Offset(w * 0.72f, h * 0.5f),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
            drawLine(
                color = color,
                start = Offset(w * 0.72f, h * 0.5f),
                end = Offset(w * 0.3f, h * 0.82f),
                strokeWidth = stroke,
                cap = StrokeCap.Round,
            )
        }
    }
}

